import React from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Formik } from "formik";
import PhoneInput from "react-phone-number-input";
import { applyOpenTalentJob } from "../../../../redux/features/find-a-job/actions/authAction";
import PdfOrImageUploader from "../../../pdfImage uploader";
import ErrorMessage from "../../../form/ErrorMessage";
import { getProfile } from "../../../../utils/pip";
import { applyJobSchema } from "../../../../utils/schema";
import TagInput from "../../../form/TagInput";
import "./ApplyNowSignupModal.css";

const normalizePhoneValue = (value) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("+")) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  return `+${digits}`;
};

const splitList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const getInitialLocation = (openTalent = {}, profileData = {}) => {
  if (String(openTalent?.location || "").trim()) return String(openTalent.location).trim();
  const city = String(openTalent?.city || profileData?.city || "").trim();
  const country = String(openTalent?.country || profileData?.country || "").trim();
  return [city, country].filter(Boolean).join(", ");
};

const normalizeSingleAvailability = (value) => {
  const resolved = splitList(value);
  return resolved[0] || "";
};

const normalizeDateInputValue = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getTodayDateInputValue = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const sanitizeAvailabilityDate = (value, minimumDate) => {
  const normalized = normalizeDateInputValue(value);
  if (!normalized) return "";
  return normalized < minimumDate ? minimumDate : normalized;
};

const ApplyNowPrefilledModal = ({
  isOpen = true,
  onClose,
  onSubmit,
  onOpenSignIn,
  formData = {},
}) => {
  const dispatch = useDispatch();
  const { isLoading, freelancerProfileData } = useSelector(
    (state) => state?.freelancer?.auth || {}
  );
  const profileData =
    freelancerProfileData && Object.keys(freelancerProfileData).length
      ? freelancerProfileData
      : getProfile("freelancer") || {};
  const openTalent = profileData?.open_talent || {};
  const experiencesList = Array.isArray(profileData?.experiences)
    ? profileData.experiences
    : [];
  const mostRecentExperience =
    experiencesList.find((item) => item?.currently_working) ||
    [...experiencesList].sort((a, b) => {
      const dateA = new Date(a?.start_date || a?.created_at || 0).getTime();
      const dateB = new Date(b?.start_date || b?.created_at || 0).getTime();
      return dateB - dateA;
    })[0];
  const cvValue = profileData?.cv || openTalent?.cv;
  const existingCvList = Array.isArray(cvValue) ? cvValue : cvValue ? [cvValue] : [];
  const [apiError, setApiError] = React.useState("");
  const minimumAvailabilityDate = React.useMemo(() => getTodayDateInputValue(), []);
  const [guestFormValues, setGuestFormValues] = React.useState({
    full_name: formData?.full_name || profileData?.full_name || "",
    email: formData?.email || profileData?.email || "",
    phone_number: openTalent?.phone_number || profileData?.phone_number || "+91",
    location: getInitialLocation(openTalent, profileData),
    years_of_experience: openTalent?.years_of_experience || "",
    availability: normalizeDateInputValue(normalizeSingleAvailability(openTalent?.availability)),
    skills: splitList(openTalent?.skills || openTalent?.key_skills),
    linkedin_url:
      openTalent?.linkedin_profile || profileData?.linkedin_url || "",
    portfolio_url:
      openTalent?.website_or_portfolio ||
      openTalent?.portfolio_website ||
      profileData?.portfolio_url ||
      "",
    cover_letter: "",
    resume_files: existingCvList,
  });
  const [guestFormErrors, setGuestFormErrors] = React.useState({});
  const isLoggedInFreelancerFlow = formData?.useMyJobStyle === true;

  const workOptions = [
    { label: "Remote", value: "Remote Freelance" },
    { label: "Full-time", value: "Full-time Freelance" },
    { label: "Part-time", value: "Part-time Freelance" },
    { label: "Hybrid", value: "Hybrid Freelance" },
    { label: "On-Site", value: "On-Site Freelance" },
    { label: "Project-Based", value: "Project Based" },
  ];

  const getApplyInitialValues = () => {
    const fullName =
      formData?.full_name ||
      profileData?.full_name ||
      `${profileData?.first_name || ""} ${profileData?.last_name || ""}`.trim();
    const currentPosition =
      mostRecentExperience?.job_title ||
      openTalent?.primary_role ||
      openTalent?.talent_role ||
      "";

    return {
      full_name: fullName || "",
      email: formData?.email || profileData?.email || "",
      phone_number: normalizePhoneValue(
        openTalent?.phone_number || profileData?.phone_number || ""
      ),
      expected_salary: openTalent?.salary_preference || "",
      work_type: openTalent?.availability || "",
      current_position: currentPosition,
      linkedin_url:
        openTalent?.linkedin_profile ||
        profileData?.linkedin_profile ||
        profileData?.linkedin_url ||
        "",
      portfolio_url:
        openTalent?.website_or_portfolio ||
        openTalent?.portfolio_website ||
        openTalent?.portfolio ||
        profileData?.portfolio_url ||
        "",
      cover_letter: "",
      resume_files: existingCvList,
    };
  };

  React.useEffect(() => {
    if (isLoggedInFreelancerFlow) return;
    setGuestFormValues((prev) => ({
      ...prev,
      full_name: prev?.full_name || formData?.full_name || profileData?.full_name || "",
      email: prev?.email || formData?.email || profileData?.email || "",
      phone_number:
        prev?.phone_number ||
        openTalent?.phone_number ||
        profileData?.phone_number ||
        "+91",
      location: prev?.location || getInitialLocation(openTalent, profileData),
      years_of_experience:
        prev?.years_of_experience || openTalent?.years_of_experience || "",
      availability: sanitizeAvailabilityDate(
        prev?.availability ||
          normalizeDateInputValue(normalizeSingleAvailability(openTalent?.availability)),
        minimumAvailabilityDate
      ),
      skills:
        prev?.skills ||
        splitList(openTalent?.skills || openTalent?.key_skills),
      linkedin_url:
        prev?.linkedin_url ||
        openTalent?.linkedin_profile ||
        profileData?.linkedin_url ||
        "",
      portfolio_url:
        prev?.portfolio_url ||
        openTalent?.website_or_portfolio ||
        openTalent?.portfolio_website ||
        profileData?.portfolio_url ||
        "",
      resume_files: prev?.resume_files?.length ? prev.resume_files : existingCvList,
    }));
  }, [existingCvList, formData?.email, formData?.full_name, isLoggedInFreelancerFlow, openTalent, profileData]);

  const handleOverlayClick = (event) => {
    event.stopPropagation();
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const resolveResumeValue = (resumeFiles = []) => {
    const firstItem = resumeFiles?.[0];
    if (!firstItem) return null;
    if (firstItem instanceof File) return firstItem;
    if (typeof firstItem === "string") return firstItem;
    if (firstItem?.file instanceof File) return firstItem.file;
    if (typeof firstItem?.file === "string") return firstItem.file;
    if (typeof firstItem?.url === "string") return firstItem.url;
    if (typeof firstItem?.preview === "string") return firstItem.preview;
    return null;
  };

  const handleSubmit = (values) => {
    setApiError("");
    const payload = new FormData();
    payload.append("job_id", String(formData?.job_id || ""));
    payload.append("full_name", values.full_name);
    payload.append("email", values.email);
    payload.append("phone_number", values.phone_number);
    payload.append("expected_salary", values.expected_salary);
    payload.append("work_type", values.work_type);
    payload.append("current_position", values.current_position);
    payload.append("linkedin_url", values.linkedin_url);
    payload.append("portfolio_url", values.portfolio_url);
    payload.append("cover_letter", values.cover_letter);
    const resumeValue = resolveResumeValue(values.resume_files);
    if (resumeValue) {
      payload.append("resume_url", resumeValue);
    }

    dispatch(
      applyOpenTalentJob({
        payload,
        token: formData?.auth_token || "",
        callback: (response) => {
          const isSuccess = response?.success === true || response?.status === true;
          if (!isSuccess) {
            setApiError(response?.message || "Failed to submit application.");
            return;
          }
          if (isSuccess && onSubmit) {
            onSubmit(response);
          }
        },
      })
    );
  };

  const validateGuestForm = () => {
    const errors = {};
    const checkUrl = (value) => {
      try {
        new URL(value);
        return true;
      } catch (e) {
        return false;
      }
    };

    if (!String(guestFormValues.phone_number || "").trim()) {
      errors.phone_number = "Please enter phone number";
    }
    if (!String(guestFormValues.location || "").trim()) {
      errors.location = "Please enter your location";
    }
    if (!String(guestFormValues.years_of_experience || "").trim()) {
      errors.years_of_experience = "Please select experience";
    }
    if (!String(guestFormValues.availability || "").trim()) {
      errors.availability = "Please select availability";
    } else if (guestFormValues.availability < minimumAvailabilityDate) {
      errors.availability = "Availability cannot be a past date";
    }
    if (!Array.isArray(guestFormValues.skills) || guestFormValues.skills.length === 0) {
      errors.skills = "Please enter skills";
    }
    if (
      String(guestFormValues.linkedin_url || "").trim() &&
      !checkUrl(String(guestFormValues.linkedin_url || "").trim())
    ) {
      errors.linkedin_url = "Please enter valid Linkedin profile link";
    }
    if (
      String(guestFormValues.portfolio_url || "").trim() &&
      !checkUrl(String(guestFormValues.portfolio_url || "").trim())
    ) {
      errors.portfolio_url = "Please enter valid portfolio link";
    }
    if (!String(guestFormValues.cover_letter || "").trim()) {
      errors.cover_letter = "Please enter cover letter";
    }
    if (!Array.isArray(guestFormValues.resume_files) || guestFormValues.resume_files.length === 0) {
      errors.resume_files = "Please upload resume";
    }

    return errors;
  };

  const updateGuestField = (key, value) => {
    setGuestFormValues((prev) => ({ ...prev, [key]: value }));
    setGuestFormErrors((prev) => ({ ...prev, [key]: "" }));
    setApiError("");
  };

  const handleGuestSubmit = (event) => {
    event.preventDefault();
    const errors = validateGuestForm();
    setGuestFormErrors(errors);
    setApiError("");
    if (Object.keys(errors).length) return;

    const payload = new FormData();
    payload.append("job_id", String(formData?.job_id || ""));
    payload.append("full_name", String(guestFormValues.full_name || ""));
    payload.append("email", String(guestFormValues.email || ""));
    payload.append("phone_number", String(guestFormValues.phone_number || ""));
    payload.append("location", String(guestFormValues.location || ""));
    payload.append("years_of_experience", String(guestFormValues.years_of_experience || ""));
    payload.append("availability", String(guestFormValues.availability || ""));
    payload.append("skills", (guestFormValues.skills || []).join(", "));
    payload.append("linkedin_url", String(guestFormValues.linkedin_url || ""));
    payload.append("portfolio_url", String(guestFormValues.portfolio_url || ""));
    payload.append("cover_letter", String(guestFormValues.cover_letter || ""));
    payload.append("work_type", String(guestFormValues.availability || ""));
    payload.append("current_position", String(guestFormValues.location || ""));
    const resumeValue = resolveResumeValue(guestFormValues.resume_files);
    if (resumeValue) {
      payload.append("resume_url", resumeValue);
    }

    dispatch(
      applyOpenTalentJob({
        payload,
        token: formData?.auth_token || "",
        callback: (response) => {
          const isSuccess = response?.success === true || response?.status === true;
          if (!isSuccess) {
            setApiError(response?.message || "Failed to submit application.");
            return;
          }
          if (onSubmit) {
            onSubmit(response);
          }
        },
      })
    );
  };

  return createPortal(
    <div className="job-modal-overlay" onClick={handleOverlayClick}>
      <div
        className="job-modal job-apply-modal"
        role="dialog"
        aria-modal="true"
      >
        <div className="job-modal-header">
          <div>
            <h2 className="job-modal-title ct_fs_30">
              {formData?.job_title || "[Job Title]"}
            </h2>
            <p className="mb-0 ct_grey_text">
              {formData?.company_name || "[Company Name]"}
            </p>
          </div>

          <button
            type="button"
            className="job-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4.66699 4.66699L11.3337 11.3337M11.3337 4.66699L4.66699 11.3337"
                stroke="#023047"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="job-modal-body">
          {isLoggedInFreelancerFlow ? (
            <Formik
              initialValues={getApplyInitialValues()}
              validationSchema={applyJobSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit: submitForm,
                setFieldValue,
                setTouched,
              }) => (
                <form className="  px-2 pb-3">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          placeholder="e.g"
                          name="full_name"
                          value={values.full_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <ErrorMessage
                          errors={errors}
                          touched={touched}
                          fieldName="full_name"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Email Address
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          placeholder="Email address"
                          name="email"
                          value={values.email}
                          readOnly
                          disabled
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Phone Number
                        </label>
                        <PhoneInput
                          id="phone_number"
                          className="ct_phone_input"
                          defaultCountry="IN"
                          value={values.phone_number}
                          onChange={(val) => {
                            setApiError("");
                            setFieldValue("phone_number", val ?? "");
                          }}
                          onBlur={() =>
                            setTouched({ ...touched, phone_number: true })
                          }
                        />
                        <ErrorMessage
                          errors={errors}
                          touched={touched}
                          fieldName="phone_number"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Expected Salary (in local currency)
                        </label>
                        <input
                          type="number"
                          className="form-control ct_input"
                          placeholder="Expected salary"
                          name="expected_salary"
                          value={values.expected_salary}
                          onChange={(event) => {
                            setApiError("");
                            handleChange(event);
                          }}
                          onBlur={handleBlur}
                        />
                        <ErrorMessage
                          errors={errors}
                          touched={touched}
                          fieldName="expected_salary"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Work
                        </label>
                        <div className="d-flex flex-column gap-2 position-relative">
                          <select
                            className="form-control ct_input"
                            name="work_type"
                            value={values.work_type}
                            onChange={(event) => {
                              setApiError("");
                              handleChange(event);
                            }}
                            onBlur={handleBlur}
                          >
                            <option value="">Select Work</option>
                            {workOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <i className="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                        </div>
                        <ErrorMessage
                          errors={errors}
                          touched={touched}
                          fieldName="work_type"
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Current Position (if unemployed, then "Unemployed")
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          placeholder="Current position"
                          name="current_position"
                          value={values.current_position}
                          onChange={(event) => {
                            setApiError("");
                            handleChange(event);
                          }}
                          onBlur={handleBlur}
                        />
                        <ErrorMessage
                          errors={errors}
                          touched={touched}
                          fieldName="current_position"
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Linkedin Profile Link
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          placeholder="Link"
                          name="linkedin_url"
                          value={values.linkedin_url}
                          onChange={(event) => {
                            setApiError("");
                            handleChange(event);
                          }}
                          onBlur={handleBlur}
                        />
                        <ErrorMessage
                          errors={errors}
                          touched={touched}
                          fieldName="linkedin_url"
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Porfolio Link
                        </label>
                        <input
                          type="text"
                          className="form-control ct_input"
                          placeholder="Link"
                          name="portfolio_url"
                          value={values.portfolio_url}
                          onChange={(event) => {
                            setApiError("");
                            handleChange(event);
                          }}
                          onBlur={handleBlur}
                        />
                        <ErrorMessage
                          errors={errors}
                          touched={touched}
                          fieldName="portfolio_url"
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Cover Letter
                        </label>
                        <textarea
                          className="form-control ct_input h-auto"
                          rows={4}
                          placeholder="Type here..."
                          name="cover_letter"
                          value={values.cover_letter}
                          onChange={(event) => {
                            setApiError("");
                            handleChange(event);
                          }}
                          onBlur={handleBlur}
                        ></textarea>
                        <ErrorMessage
                          errors={errors}
                          touched={touched}
                          fieldName="cover_letter"
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group mb-4">
                        <label className="ct_fs_14 ct_darkblue_text mb-2">
                          Upload Resume
                        </label>
                        <PdfOrImageUploader
                          maxFiles={1}
                          multiple={false}
                          acceptType="application/pdf,.pdf"
                          placeholder="Upload CV (PDF only)"
                          replaceOnSelect={true}
                          existingFiles={
                            values?.resume_files &&
                            Array.isArray(values.resume_files)
                              ? values.resume_files.map((file, idx) => ({
                                  id: file?.id || idx,
                                  file: file,
                                  name:
                                    file?.name ||
                                    (typeof file === "string"
                                      ? file.split("/").pop()
                                      : `cv-${idx + 1}.pdf`),
                                  type: "pdf",
                                  preview:
                                    typeof file === "string"
                                      ? file
                                      : file?.preview || file?.url || "",
                                  isExisting: !(file instanceof File),
                                }))
                              : []
                          }
                          onChange={(newFiles) => {
                            setApiError("");
                            setTouched({ ...touched, resume_files: true });
                            setFieldValue("resume_files", newFiles);
                          }}
                        />
                        <ErrorMessage
                          errors={errors}
                          touched={touched}
                          fieldName="resume_files"
                        />
                      </div>
                    </div>
                  </div>
                  {apiError ? (
                    <p className="ct_modal_input_error mt-1">{apiError}</p>
                  ) : null}
                  <div className="d-flex align-items-center gap-3 justify-content-end pt-4">
                    <button
                      className="ct_sky_blue_btn ct_yellow_btn ct_outline_grey px-5 py-2"
                      type="button"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      className="ct_sky_blue_btn ct_yellow_btn px-5 py-2"
                      type="button"
                      onClick={submitForm}
                      disabled={isLoading}
                    >
                      {isLoading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          ) : (
            <form className="job-modal-body" onSubmit={handleGuestSubmit}>
              <div className="job-grid">
                <div className="job-field">
                  <label className="job-label">Phone Number</label>
                  <div className="job-phone-row">
                    <PhoneInput
                      id="phone_number"
                      className=" job-input ct_phone_input_no_14 job-phone-input-wrap"
                      defaultCountry="IN"
                      international
                      countryCallingCodeEditable
                      value={guestFormValues.phone_number}
                      onChange={(value) =>
                        updateGuestField("phone_number", value || "")
                      }
                    />
                  </div>
                  {guestFormErrors.phone_number ? (
                    <p className="ct_modal_input_error">
                      {guestFormErrors.phone_number}
                    </p>
                  ) : null}
                </div>

                <div className="job-field">
                  <label className="job-label">Your Location</label>
                  <input
                    className="job-input"
                    type="text"
                    placeholder="Enter Location"
                    value={guestFormValues.location}
                    onChange={(event) =>
                      updateGuestField("location", event.target.value)
                    }
                  />
                  {guestFormErrors.location ? (
                    <p className="ct_modal_input_error">
                      {guestFormErrors.location}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="job-grid">
                <div className="job-field">
                  <label className="job-label">Experience</label>
                  <div className="job-select-wrap">
                    <select
                      value={guestFormValues.years_of_experience}
                      className="job-input job-select"
                      onChange={(event) =>
                        updateGuestField(
                          "years_of_experience",
                          event.target.value,
                        )
                      }
                    >
                      <option value="">Select experience</option>
                      <option value="0-2 years">0-2 years</option>
                      <option value="3-5 years">3-5 years</option>
                      <option value="6-10 years">6-10 years</option>
                      <option value="10+ years">10+ years</option>
                    </select>
                    <span className="job-chevron">
                      <svg width="14" height="9" viewBox="0 0 14 9" fill="none">
                        <path
                          d="M1.16699 1.25L7.00033 7.08333L12.8337 1.25"
                          stroke="#023047"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                  {guestFormErrors.years_of_experience ? (
                    <p className="ct_modal_input_error">
                      {guestFormErrors.years_of_experience}
                    </p>
                  ) : null}
                </div>
                <div className="job-field">
                  <label className="job-label">Your Availability</label>
                  <div className="job-date-wrap">
                    <input
                      id="availability_date"
                      className="job-input job-date-input"
                      type="date"
                      min={minimumAvailabilityDate}
                      value={guestFormValues.availability}
                      onChange={(event) =>
                        updateGuestField(
                          "availability",
                          sanitizeAvailabilityDate(
                            event.target.value,
                            minimumAvailabilityDate
                          )
                        )
                      }
                      onBlur={(event) =>
                        updateGuestField(
                          "availability",
                          sanitizeAvailabilityDate(
                            event.target.value,
                            minimumAvailabilityDate
                          )
                        )
                      }
                    />
                  </div>
                  {guestFormErrors.availability ? (
                    <p className="ct_modal_input_error">
                      {guestFormErrors.availability}
                    </p>
                  ) : null}
                </div>
              </div>

              <TagInput
                label="Skills"
                placeholder="Type to add skills..."
                name="skills"
                value={guestFormValues.skills}
                onChange={updateGuestField}
                error={guestFormErrors.skills}
                addOnBlur={true}
              />

              <div className="job-field job-field-full">
                <label className="job-label">
                  Linkedin Profile Link (Optional)
                </label>
                <input
                  className="job-input"
                  type="url"
                  value={guestFormValues.linkedin_url}
                  onChange={(event) =>
                    updateGuestField("linkedin_url", event.target.value)
                  }
                />
                {guestFormErrors.linkedin_url ? (
                  <p className="ct_modal_input_error">
                    {guestFormErrors.linkedin_url}
                  </p>
                ) : null}
              </div>

              <div className="job-field job-field-full">
                <label className="job-label">Portfolio Link (Optional)</label>
                <input
                  className="job-input"
                  type="url"
                  value={guestFormValues.portfolio_url}
                  onChange={(event) =>
                    updateGuestField("portfolio_url", event.target.value)
                  }
                />
                {guestFormErrors.portfolio_url ? (
                  <p className="ct_modal_input_error">
                    {guestFormErrors.portfolio_url}
                  </p>
                ) : null}
              </div>

              <div className="job-field job-field-full">
                <label className="job-label">Cover Letter</label>
                <textarea
                  className="job-input job-textarea h-auto"
                  rows={4}
                  value={guestFormValues.cover_letter}
                  onChange={(event) =>
                    updateGuestField("cover_letter", event.target.value)
                  }
                />
                {guestFormErrors.cover_letter ? (
                  <p className="ct_modal_input_error">
                    {guestFormErrors.cover_letter}
                  </p>
                ) : null}
              </div>

              <div className="job-field job-field-full">
                <label className="job-label">Upload Resume</label>
                <PdfOrImageUploader
                  maxFiles={1}
                  multiple={false}
                  acceptType="application/pdf,.pdf"
                  placeholder="Upload CV (PDF only)"
                  replaceOnSelect={true}
                  existingFiles={
                    guestFormValues?.resume_files &&
                    Array.isArray(guestFormValues.resume_files)
                      ? guestFormValues.resume_files.map((file, idx) => ({
                          id: file?.id || idx,
                          file: file,
                          name:
                            file?.name ||
                            (typeof file === "string"
                              ? file.split("/").pop()
                              : `cv-${idx + 1}.pdf`),
                          type: "pdf",
                          preview:
                            typeof file === "string"
                              ? file
                              : file?.preview || file?.url || "",
                          isExisting: !(file instanceof File),
                        }))
                      : []
                  }
                  onChange={(newFiles) =>
                    updateGuestField("resume_files", newFiles)
                  }
                />
                <ErrorMessage
                  errors={guestFormErrors}
                  touched={{ resume_files: true }}
                  fieldName="resume_files"
                />
              </div>

              {apiError ? (
                <p className="ct_modal_input_error mt-1">{apiError}</p>
              ) : null}

              <div className="job-submit-wrap">
                <button
                  className="job-submit-btn"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? "Please wait..." : "Submit"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ApplyNowPrefilledModal;
